// Over the board: each landing of the project that waits for a person's
// approval, one line with its issue; "Review and approve" opens today's
// approval control, what exactly main moves to and "Approve with passkey"
// (store.approveLanding), so nothing is approved unseen. With nothing waiting, nothing shows. An
// approval taken is said in a toast, since its strip leaves with it; one not
// taken is said in the strip, which asks again. A link to its proposal
// (`#proposal-<id>`, queue/words.ts) opens it.
//
// A batch that a person merges together (merge.ts) has one strip while it
// runs or waits: "Merging <keys>", and its steps, Combine · Check · Approve ·
// Land, the one it is at marked; waiting, it names the landing it waits
// for. Its approval is asked there, with the same "Review and approve". A
// batch that stopped says why in one line, until the person dismisses it;
// the cards it holds say it still.
//
// A batch that stopped has its PM agent (Poulpe-406), a session Poulpe starts
// for it: one line under its batch says its name, from the project's
// setting, and what it is doing, from its session, or that it asks the
// person, and Answer opens its session, where the question docks. Open its
// session, Stop (confirmed, `stop_session`), and Ask the PM agent…, a field
// whose words are its session's next prompt (`prompt_session`), said in a
// toast. A retry it makes is a batch of its own, shown after the stopped
// batch while that still holds a proposal; its PM agent's line is under the
// last of them, once.
//
// Then each question put to the person by a session the board does not
// hold, which has no pane to dock in (code/QuestionDock.tsx): "Asks you",
// its flags (questions/waiting.tsx), and Answer opens its card here. On its issue's project's board, or on
// every board when its issue is not known, so it is never lost.
import { LiveChecks } from '../checks/progress'
import { Fragment, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { errorMessage } from '../api/client'
import type { ApprovalAsked, Landing, Question } from '../api/types'
import { uuid4 } from '../api/uuid'
import { CheckIcon } from '../components/Icons'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { Working } from '../components/Working'
import { useSessionPath } from '../projects/ProjectRoute'
import { askTitle } from '../questions/asks'
import { QuestionCard } from '../questions/QuestionCard'
import { QuestionFlags, useExpiryClock } from '../questions/waiting'
import { Approval } from '../queue/Approval'
import { CarriedIssues } from '../queue/Carried'
import { approvalSaid, carriedKeys, proposalAnchor, short, waitingName, type ApprovalSaid } from '../queue/words'
import { useAttempt } from '../sessions/attempt'
import { useBoardState, useStore } from '../store/context'
import { landingOf, needsYou } from '../store/store'
import { cx } from '../transcript/bits'
import { approvalLandings } from './live'
import { batchLandings, isBatch, keysText, MERGE_STEPS, pmDoing, pmName, pmOf, retriesOf, STEP_WORDS, stepOf, stoppedText, waitingText } from './merge'
import './ready.css'

export function ApprovalStrip({ project }: { project: string | null }) {
  const { landings, held, dismissed, questions, pendingAnswers, sessions, issues, me } = useBoardState()
  const asking = approvalLandings(project, landings).filter((landing) => !isBatch(landing))
  const batches = batchLandings(project, landings)
  const stopped = [...held.values()].filter((landing) => landing.project_id === project && !dismissed.has(landing.id))
  const member = me?.member?.id
  // Oldest first, as the dock pages them.
  const unheld = useMemo(() => needsYou(questions, member, pendingAnswers).filter((question) => {
    if (sessions.has(question.session)) return false
    const issue = question.issue ? issues.get(question.issue) : undefined
    return !issue || issue.project === project
  }).reverse(), [questions, member, pendingAnswers, sessions, issues, project])
  if (asking.length === 0 && batches.length === 0 && stopped.length === 0 && unheld.length === 0) return null
  // A retry shows after the stopped batch it retries; the other batches first, as before.
  const retryUnder = new Map(stopped.flatMap((landing) => {
    const pm = pmOf(landing)
    return pm ? [[pm, landing.id] as const] : []
  }))
  const groups = stopped.map((landing) => ({
    landing,
    retries: retryUnder.get(pmOf(landing) ?? '') === landing.id
      ? retriesOf(landing, landings).filter((l) => l.project_id === project) : [],
  }))
  const beside = new Set(groups.flatMap(({ retries }) => retries.map((l) => l.id)))
  const first = batches.filter((landing) => !beside.has(landing.id))
  // Each PM agent's line once, under the last of its landings shown.
  const shown = [...first, ...groups.flatMap(({ landing, retries }) => [landing, ...retries])]
  const lineUnder = new Map(shown.flatMap((landing) => {
    const pm = pmOf(landing)
    return pm ? [[pm, landing.id] as const] : []
  }))
  const pmLine = (landing: Landing) => {
    const pm = pmOf(landing)
    return pm && lineUnder.get(pm) === landing.id ? <PmLine landing={landing} session={pm} /> : null
  }
  return <>
    {first.map((landing) => <BatchStrip key={landing.id} landing={landing}>{pmLine(landing)}</BatchStrip>)}
    {asking.map((landing) => landing.waiting_for?.for === 'approval'
      ? <LandingStrip key={landing.id} landing={landing} asked={landing.waiting_for} /> : null)}
    {groups.map(({ landing, retries }) => <Fragment key={landing.id}>
      <StoppedStrip landing={landing}>{pmLine(landing)}</StoppedStrip>
      {retries.map((retry) => <BatchStrip key={retry.id} landing={retry}>{pmLine(retry)}</BatchStrip>)}
    </Fragment>)}
    {unheld.map((question) => <QuestionStrip key={question.id} question={question} />)}
  </>
}

/** Approve with passkey, for a landing's strip: busy while it is sent, and what the strip says of an approval not taken. */
function useApprove(landing: Landing) {
  const store = useStore()
  const mounted = useMounted()
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<ApprovalSaid | null>(null)
  const approve = async (asked: ApprovalAsked) => {
    if (busy) return
    setBusy(true)
    setSaid(null)
    const outcome = await store.approveLanding(landing.id, asked)
    const words = approvalSaid(outcome)
    const still = store.getState().landings.get(landing.id)?.waiting_for?.for === 'approval'
    if (words.tone === 'ok' || !still || !mounted.current) toast(words.text, words.tone === 'error' ? 'error' : 'info')
    else setSaid(words)
    if (mounted.current) setBusy(false)
  }
  return { busy, said, approve }
}

/** Opened by a link to its first proposal. */
function useOpened(landing: Landing) {
  const [open, setOpen] = useState(false)
  const { hash, key } = useLocation()
  const named = hash === `#${proposalAnchor(landing.proposal_id)}`
  useEffect(() => { if (named) setOpen(true) }, [named, key])
  return [open, setOpen] as const
}

/** A proposal by the keys of every issue it carries (Poulpe-554), or its short id while the page does not hold it. */
function useKeyOf(): (id: string) => string {
  const { proposals, issues } = useBoardState()
  return (id: string) => {
    const proposal = proposals.get(id)
    return proposal ? keysText(carriedKeys(proposal, issues)) : short(id)
  }
}

/** Every issue's key that each proposal carries, in order. */
function useKeys(landing: Landing): string[] {
  const { proposals, issues } = useBoardState()
  return landing.proposals.flatMap((id) => {
    const proposal = proposals.get(id)
    return proposal ? carriedKeys(proposal, issues) : [short(id)]
  })
}

function LandingStrip({ landing, asked }: { landing: Landing; asked: ApprovalAsked }) {
  const { proposals, issues } = useBoardState()
  const { busy, said, approve } = useApprove(landing)
  const [open, setOpen] = useOpened(landing)
  const proposal = proposals.get(landing.proposal_id)
  const issue = proposal ? issues.get(proposal.issue_id) : undefined
  const keys = proposal ? keysText(carriedKeys(proposal, issues)) : null
  // Its first issue with its title, then every other issue it carries (Poulpe-554).
  const more = proposal && proposal.issue_ids.length > 1
  return <section className="board-strip" id={proposalAnchor(landing.proposal_id)} aria-label={`${keys ?? 'A landing'} waits for your approval`}>
    <div className="board-strip-head">
      <span className="board-strip-flag">Needs you</span>
      {issue ? <><Link className="board-strip-key" to={`/${encodeURIComponent(issue.key)}`}>{issue.key}</Link><span className="board-strip-title">{issue.title}</span></>
        : <span className="board-strip-title">{proposal ? <code title="An issue this page has not loaded yet">{proposal.issue_id}</code> : 'A landing'}</span>}
      {more && <span className="merge-strip-what">with <CarriedIssues proposal={proposal} className="board-strip-key" skipFirst /></span>}
      <button type="button" className="button board-strip-toggle" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
        {open ? 'Hide' : 'Review and approve'}
      </button>
    </div>
    {landing.state === 'deferred' && <p className="queue-said">{waitingName(landing)}</p>}
    {said && <p className={`queue-said is-${said.tone}`} role={said.tone === 'error' ? 'alert' : 'status'}>{said.text}</p>}
    {open && <Approval asked={asked} busy={busy} onApprove={() => void approve(asked)} />}
  </section>
}

/** A batch that runs or waits: what it merges, its steps, and its approval when it asks for one; then its PM agent's line, if given. */
function BatchStrip({ landing, children }: { landing: Landing; children?: ReactNode }) {
  const { landings, proposals } = useBoardState()
  const keys = useKeys(landing)
  const { busy, said, approve } = useApprove(landing)
  const [open, setOpen] = useOpened(landing)
  const asked = landing.waiting_for?.for === 'approval' ? landing.waiting_for : null
  const step = stepOf(landing)
  const running = landing.state === 'waiting' ? landingOf(landings, landing.project_id) : null
  const keyOf = useKeyOf()
  const runningKey = running && proposals.has(running.proposal_id) ? keyOf(running.proposal_id) : null
  return <section className={cx('board-strip merge-strip', !asked && 'is-quiet')} id={proposalAnchor(landing.proposal_id)} aria-label={`Merging ${keysText(keys)}`}>
    <div className="board-strip-head">
      {asked && <span className="board-strip-flag">Needs you</span>}
      <span className="merge-strip-what">Merging {keys.map((key, i) => <Fragment key={i}>
        {i > 0 && (i === keys.length - 1 ? ' and ' : ', ')}<Link className="board-strip-key" to={`/${encodeURIComponent(key)}`}>{key}</Link>
      </Fragment>)}</span>
      <ol className="merge-steps" aria-label="Steps">
        {!step && <li className="is-now" aria-current="step">{waitingText(runningKey)}</li>}
        {MERGE_STEPS.map((one, i) => {
          const at = step ? MERGE_STEPS.indexOf(step) : -1
          const done = i < at
          return <li key={one} className={cx(done && 'is-done', i === at && 'is-now')} aria-current={i === at ? 'step' : undefined}>
            {done ? <CheckIcon size={12} /> : <span className="merge-step-dot" aria-hidden="true" />}{STEP_WORDS[one]}
          </li>
        })}
      </ol>
      <span className="board-strip-actions">
        {asked && <button type="button" className="button board-strip-toggle" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
          {open ? 'Hide' : 'Review and approve'}
        </button>}
      </span>
    </div>
    <LiveChecks landing={landing.id} />
    {children}
    {landing.state === 'deferred' && <p className="queue-said">{waitingName(landing)}</p>}
    {said && <p className={`queue-said is-${said.tone}`} role={said.tone === 'error' ? 'alert' : 'status'}>{said.text}</p>}
    {open && asked && <Approval asked={asked} busy={busy} onApprove={() => void approve(asked)} />}
  </section>
}

/** A batch that stopped and holds its proposals: why, in one line, and Dismiss; then its PM agent's line, if given. */
function StoppedStrip({ landing, children }: { landing: Landing; children?: ReactNode }) {
  const store = useStore()
  const keys = useKeys(landing)
  const keyOf = useKeyOf()
  const why = stoppedText(landing.outcome, keyOf) ?? 'It stopped'
  return <section className="board-strip merge-strip is-stopped" aria-label={`The merge of ${keysText(keys)} stopped`}>
    <div className="board-strip-head">
      <span className="board-strip-flag">Stopped</span>
      <span className="merge-strip-what">The merge of {keysText(keys)}</span>
      <span className="merge-strip-why">{why}</span>
      <span className="board-strip-actions">
        <button type="button" className="button button-quiet" onClick={() => store.dismissLanding(landing.id)}>Dismiss</button>
      </span>
    </div>
    <LiveChecks landing={landing.id} />
    {children}
  </section>
}

const STOP_QUESTION = 'Stop the PM agent? Its session ends and takes no more prompts.'

/**
 * A batch's PM agent: its name and what it is doing, or that it asks the
 * person; Open its session (Answer, when it asks), Stop, and Ask the PM
 * agent…, an inline field: Enter sends, Escape closes it.
 */
function PmLine({ landing, session: id }: { landing: Landing; session: string }) {
  const store = useStore()
  const { sessions, projects, questions, me, offline } = useBoardState()
  const mounted = useMounted()
  const pathOf = useSessionPath()
  const attempt = useAttempt()
  const fieldId = useId()
  const askButton = useRef<HTMLButtonElement>(null)
  const field = useRef<HTMLInputElement>(null)
  const [asking, setAsking] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [stopping, setStopping] = useState(false)
  const session = sessions.get(id)
  // Cached projects from before the setting have no PM agent field: Claude, the server's default.
  const agent = projects.get(landing.project_id)?.pm_agent?.agent ?? 'claude'
  const name = pmName(agent)
  const engine = agent === 'claude' || agent === 'codex' ? agent : undefined
  const doing = pmDoing(session)
  const ended = session?.state === 'ended'
  const asks = useMemo(() => needsYou(questions, me?.member?.id).some((q) => q.session === id), [questions, me, id])
  const path = pathOf(id)
  useEffect(() => { if (ended) setAsking(false) }, [ended])

  const close = () => {
    setAsking(false)
    setText('')
    askButton.current?.focus()
  }
  const send = async (event: FormEvent) => {
    event.preventDefault()
    const prompt = text.trim()
    if (sending || offline || !prompt) return
    setSending(true)
    const { mutationId } = attempt.ids(JSON.stringify([id, prompt]))
    try {
      await store.sendSessionOp({ mutation_id: mutationId, op: 'prompt_session', args: { session_id: id, prompt } })
      attempt.applied()
      toast('Sent to the PM agent.')
      if (mounted.current) close()
    } catch (err) {
      // The words stay, to send again.
      toast(`Could not send to the PM agent: ${errorMessage(err)}`, 'error')
      if (mounted.current) field.current?.focus()
    }
    if (mounted.current) setSending(false)
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || sending) return
    event.preventDefault()
    event.stopPropagation()
    close()
  }
  const stop = () => {
    if (stopping || store.getState().offline) return
    if (!window.confirm(STOP_QUESTION)) return
    setStopping(true)
    // It stops once its runner says so: the line says done, and Stop goes.
    store.sendSessionOp({ mutation_id: uuid4(), op: 'stop_session', args: { session_id: id } }).catch((err: unknown) => {
      toast(`Could not stop the PM agent: ${errorMessage(err)}`, 'error')
      if (mounted.current) setStopping(false)
    })
  }

  return <div className={cx('merge-pm', asks && 'is-asking')} role="group" aria-label={name}>
    <div className="merge-pm-line">
      {asks ? <span className="merge-pm-asks">The PM agent asks you</span>
        : <span className="merge-pm-what">
          {doing === 'working' && <Working label={`${name} is working`} aria-hidden />}
          <span className="merge-pm-name" data-engine={engine}>{name}</span>{doing && <> · {doing}</>}
        </span>}
      <span className="board-strip-actions">
        {asks ? <Link className="button button-small button-primary" to={path}>Answer</Link>
          : <Link className="button button-small button-quiet" to={path}>Open its session</Link>}
        {!ended && <>
          <button ref={askButton} type="button" className="button button-small" aria-expanded={asking} aria-controls={asking ? fieldId : undefined}
            onClick={() => (asking ? close() : setAsking(true))}>Ask the PM agent…</button>
          <button type="button" className="button button-small button-quiet" onClick={stop} aria-disabled={stopping || offline || undefined} {...needsServer(offline)}>
            {stopping ? 'Stopping…' : 'Stop'}
          </button>
        </>}
      </span>
    </div>
    {asking && <form className="merge-pm-ask" onSubmit={(event) => void send(event)} onKeyDown={onKeyDown}>
      <label className="visually-hidden" htmlFor={fieldId}>What to ask the PM agent</label>
      <input ref={field} id={fieldId} type="text" value={text} onChange={(event) => setText(event.target.value)} readOnly={sending}
        placeholder="Ask the PM agent…" autoFocus autoComplete="off" />
      <button type="button" className="button button-small button-quiet" onClick={close} disabled={sending}>Cancel</button>
      <button type="submit" className="button button-small button-primary" disabled={sending || offline || !text.trim()} {...needsServer(offline)}>Send</button>
    </form>}
  </div>
}

/** A question from a session the board does not hold: one line, and Answer opens its card. */
function QuestionStrip({ question }: { question: Question }) {
  const { issues } = useBoardState()
  const [open, setOpen] = useState(false)
  const now = useExpiryClock([question])
  const issue = question.issue ? issues.get(question.issue) : undefined
  return <section className="board-strip" aria-label={`${issue?.key ?? 'A session'} asks you`}>
    <div className="board-strip-head">
      <span className="board-strip-flag">Asks you</span>
      {issue && <Link className="board-strip-key" to={`/${encodeURIComponent(issue.key)}`}>{issue.key}</Link>}
      <span className="board-strip-title">{askTitle(question)}</span>
      <QuestionFlags question={question} now={now} />
      <button type="button" className="button board-strip-toggle" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
        {open ? 'Hide' : 'Answer'}
      </button>
    </div>
    {open && <QuestionCard question={question} issue={issue} flags={false} />}
  </section>
}
